import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { MailCheck } from 'lucide-react';
import { Alert } from '@/components/alert';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import portalService from '@/services/portal/portalService';
import { PortalAuthCard } from './PortalAuthCard';

const schema = Yup.object().shape({
  email: Yup.string().trim().email('Enter a valid email address.').required('Enter your email address.'),
});

/**
 * Ask for a reset link. The server answers the same whether or not the email
 * has an account, so this page always says the same thing too.
 */
const PortalForgotPassword = () => {
  const [sentMessage, setSentMessage] = useState(null);

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: schema,
    onSubmit: async (values, { setStatus }) => {
      setStatus(null);
      try {
        setSentMessage(await portalService.forgotPassword(values.email.trim()));
      } catch (error) {
        setStatus(getApiErrorMessage(error, 'Something went wrong. Please try again.'));
      }
    },
  });

  if (sentMessage) {
    return (
      <PortalAuthCard title="Check your email">
        <div className="flex flex-col items-center gap-4 text-center">
          <MailCheck size={32} className="text-primary" />
          <p className="text-2sm text-gray-700">{sentMessage}</p>
          <p className="text-2xs text-gray-500">The link works once, for 60 minutes.</p>
          <Link to="/portal/login" className="btn btn-light">Back to sign in</Link>
        </div>
      </PortalAuthCard>
    );
  }

  return (
    <PortalAuthCard title="Reset your password" subtitle="We will email you a link to choose a new password.">
      <form className="flex flex-col gap-5" onSubmit={formik.handleSubmit} noValidate>
        {formik.status && <Alert variant="danger">{formik.status}</Alert>}

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900" htmlFor="portal-reset-email">Email</label>
          <label className="input">
            <input
              id="portal-reset-email"
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              {...formik.getFieldProps('email')}
              className={clsx('form-control', { 'is-invalid': formik.touched.email && formik.errors.email })}
            />
          </label>
          {formik.touched.email && formik.errors.email && (
            <span role="alert" className="text-danger text-xs mt-1">{formik.errors.email}</span>
          )}
        </div>

        <button type="submit" className="btn btn-dark flex justify-center grow" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? 'Sending…' : 'Send reset link'}
        </button>

        <Link to="/portal/login" className="text-2sm link text-center">Back to sign in</Link>
      </form>
    </PortalAuthCard>
  );
};

export { PortalForgotPassword };
